import mixpanel from 'mixpanel-browser';
import {
  getBrowserWindow,
  getDeviceType,
  getDocumentReferrer,
  getLocationHref,
  isBrowserEnvironment,
} from '@/lib/browserUtils';

// Token del proyecto de Mixpanel de la cartilla. Se configura por entorno
// (NEXT_PUBLIC_MIXPANEL_TOKEN); sin él, el tracking queda deshabilitado.
const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || '';

// Entorno lógico (test/prod) que el deploy setea por ambiente. Se registra como
// super property `entorno` para filtrar los eventos sin depender de la URL/path.
const APP_ENV = process.env.NEXT_PUBLIC_APP_ENV || 'local';

let initialized = false;

// --- Estado de sesión para los eventos del tracking plan ---
let pageLoadTime = 0;                 // ms del primer init (aprox. carga de página)
let searchTypeChangeCount = 0;        // cuántas veces se cambió el tipo de búsqueda
let sessionSearchCount = 0;           // cuántas búsquedas se enviaron en la sesión
let lastCartillaViewedUrl = '';       // dedupe de cartilla_viewed (evita doble Strict Mode)

const getSecondsSincePageLoad = (): number =>
  pageLoadTime ? Math.round((Date.now() - pageLoadTime) / 1000) : 0;

/**
 * Deriva el `source` del ingreso: prioriza utm_source; si no, clasifica por el
 * referrer (home de medicus vs. externo); por defecto "web".
 */
function getSource(): string {
  const win = getBrowserWindow();
  if (!win) return 'web';

  const utm = new URLSearchParams(win.location.search).get('utm_source');
  if (utm) return utm;

  const referrer = getDocumentReferrer();
  if (!referrer) return 'web';
  try {
    const host = new URL(referrer).hostname;
    if (host.endsWith('medicus.com.ar') || host.endsWith('medicus.com')) return 'home medicus.com.ar';
    return 'externo';
  } catch {
    return 'web';
  }
}

/** Contexto común que acompaña a los eventos. */
function getDeviceContext() {
  return { device: getDeviceType() };
}

function getPageContext() {
  return {
    URL: getLocationHref(),
    referrer: getDocumentReferrer(),
    device: getDeviceType(),
  };
}

function isTrackAvailable(): boolean {
  return initialized && mixpanel?.track != null;
}

/** Inicializa Mixpanel una sola vez (idempotente). */
export const initMixpanel = () => {
  if (initialized || !isBrowserEnvironment()) return;

  if (!MIXPANEL_TOKEN) {
    console.warn('[Mixpanel] Falta NEXT_PUBLIC_MIXPANEL_TOKEN: el tracking queda deshabilitado.');
    return;
  }

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      // Heatmap sin session replay: se alimenta del autocapture de CLICKS.
      // Habilitamos solo `click` (para el mapa de calor) y apagamos el resto,
      // en especial dead_click/rage_click, que no queremos como eventos.
      // (record_heatmap_data se descarta: solo captura durante grabación de
      // sesión, que no usamos por privacidad — sitio de salud.)
      autocapture: {
        pageview: false,
        click: true,
        dead_click: false,
        rage_click: false,
        input: false,
        scroll: false,
        submit: false,
        capture_text_content: false,
      },
      persistence: 'localStorage',
      debug: process.env.NODE_ENV === 'development',
    });
    // Super property: se adjunta `entorno` a TODOS los eventos (incluidos los
    // $mp_click del heatmap). register (no register_once) para que siempre
    // refleje el entorno actual del deploy.
    mixpanel.register({ entorno: APP_ENV });
    initialized = true;
    pageLoadTime = Date.now();
  } catch (error) {
    console.error('[Mixpanel] Error inicializando:', error);
  }
};

/** Evento genérico. Base para los eventos del tracking plan (Excel). */
export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  try {
    if (isTrackAvailable()) {
      mixpanel.track(eventName, { ...getDeviceContext(), ...properties });
    }
  } catch (error) {
    console.error(`[Mixpanel] Error trackeando "${eventName}":`, error);
  }
};

/** Vista de página (cambio de ruta). */
export const trackPageView = (url: string) => {
  try {
    if (isTrackAvailable()) {
      mixpanel.track('Page Viewed', { page: url, ...getPageContext() });
    }
  } catch (error) {
    console.error('[Mixpanel] Error trackeando Page Viewed:', error);
  }
};

export const identifyUser = (userId: string, userProperties?: Record<string, unknown>) => {
  try {
    if (!isTrackAvailable()) return;
    mixpanel.identify(userId);
    if (userProperties) mixpanel.people.set(userProperties);
  } catch (error) {
    console.error('[Mixpanel] Error en identify:', error);
  }
};

export const resetUser = () => {
  try {
    if (isTrackAvailable()) mixpanel.reset();
  } catch (error) {
    console.error('[Mixpanel] Error en reset:', error);
  }
};

export const setUserProperties = (properties: Record<string, unknown>) => {
  try {
    if (isTrackAvailable()) mixpanel.people.set(properties);
  } catch (error) {
    console.error('[Mixpanel] Error en people.set:', error);
  }
};

/** Indica si Mixpanel quedó inicializado (útil para debug/tests). */
export const isMixpanelReady = () => initialized;

// =========================================================================
// EVENTOS — Tracking plan Pantalla 1 (Búsqueda)
// =========================================================================
// Emisor interno (guardado + try/catch) para los eventos tipados. No inyecta
// device automáticamente: cada evento manda exactamente las props del plan.
function emit(eventName: string, properties?: Record<string, unknown>) {
  try {
    if (isTrackAvailable()) {
      // Log legible en desarrollo: una línea por evento con sus props en JSON,
      // fácil de copiar. No afecta producción.
      if (process.env.NODE_ENV === 'development') {
        console.log(`[MP] ${eventName}`, JSON.stringify(properties ?? {}));
      }
      mixpanel.track(eventName, properties);
    }
  } catch (error) {
    console.error(`[Mixpanel] Error trackeando "${eventName}":`, error);
  }
}

/** #1 — Ingreso a la cartilla (base del funnel). Dedupe por URL. */
export const trackCartillaViewed = () => {
  const url = getLocationHref();
  if (url && url === lastCartillaViewedUrl) return; // evita doble disparo (Strict Mode / misma ruta)
  lastCartillaViewedUrl = url;
  emit('cartilla_viewed', {
    source: getSource(),
    device: getDeviceType(),
    referrer: getDocumentReferrer(),
    logged_in: false,   // la cartilla pública no tiene login
    plan_conocido: false,
  });
};

/** #2 — Selección del tipo de búsqueda ("Buscar por"). */
export const trackSearchTypeSelected = (searchType: string) => {
  searchTypeChangeCount += 1;
  emit('search_type_selected', {
    search_type: searchType,
    orden_de_seleccion: searchTypeChangeCount,
  });
};

/** #3 — Selección de plan. */
export const trackPlanSelected = (plan: string) => {
  emit('plan_selected', {
    plan,
    tiempo_desde_page_load: getSecondsSincePageLoad(),
  });
};

/** #4 — Selección de provincia/localidad (input Pcia/Zona o "Mi ubicación"). */
export const trackLocationSelected = (data: {
  provincia: string;
  localidad: string;
  zona?: string;
}) => {
  emit('location_selected', {
    provincia: data.provincia,
    localidad: data.localidad,
    zona: data.zona,
  });
};

/** #5 — Selección de especialidad concreta (metodo: scroll vs tipeo). */
export const trackSpecialtySelected = (data: {
  especialidad: string;
  metodo: 'scroll' | 'tipeo';
}) => {
  emit('specialty_selected', {
    especialidad: data.especialidad,
    metodo: data.metodo,
  });
};

/** #6 — Envío de la búsqueda (click en Buscar). Cierra el paso 1 del funnel. */
export const trackSearchSubmitted = (data: {
  plan: string;
  provincia: string;
  localidad: string;
  zona?: string;
  especialidad: string;
  searchType: string;
  nombreProfesional?: string;
}) => {
  sessionSearchCount += 1;
  emit('search_submitted', {
    plan: data.plan,
    provincia: data.provincia,
    localidad: data.localidad,
    zona: data.zona,
    especialidad: data.especialidad,
    search_type: data.searchType,
    nombre_profesional: data.nombreProfesional,
    numero_de_busqueda_en_sesion: sessionSearchCount,
  });
};

// =========================================================================
// EVENTOS — Tracking plan Pantalla 2 (Dropdowns / interacción con filtros)
// =========================================================================

/** #7 — Apertura de un dropdown/filtro. */
export const trackFilterOpened = (data: {
  filtro: string;
  estadoOtrosFiltros: Record<string, boolean>;
}) => {
  emit('filter_opened', {
    filtro: data.filtro,
    estado_de_los_otros_filtros: data.estadoOtrosFiltros,
  });
};

/** #8 (ORO) — Tipeo en el buscador interno de un filtro. */
export const trackFilterSearchTyped = (data: {
  filtro: string;
  textoTipeado: string;
  huboMatch: boolean;
}) => {
  emit('filter_search_typed', {
    filtro: data.filtro,
    texto_tipeado: data.textoTipeado,
    hubo_match: data.huboMatch,
  });
};

/** #9 — Se abre un dropdown y se cierra sin seleccionar nada. */
export const trackFilterAbandoned = (data: {
  filtro: string;
  tiempoAbierto: number; // segundos
  scrolleo: boolean;
  tipeo: boolean;
}) => {
  emit('filter_abandoned', {
    filtro: data.filtro,
    tiempo_abierto: data.tiempoAbierto,
    scrolleo: data.scrolleo,
    tipeo: data.tipeo,
  });
};

// =========================================================================
// EVENTOS — Tracking plan Pantalla 3 (Resultados + mapa)
// =========================================================================

// Filtros de la última búsqueda: se setean al buscar y los reusan los eventos
// de resultados que ocurren en otros componentes (pdf, paginación) sin
// prop-drilling.
let currentSearchFilters: Record<string, unknown> = {};
export const setCurrentSearchFilters = (filtros: Record<string, unknown>) => {
  currentSearchFilters = filtros;
};

/** #10 — El backend devolvió resultados (o 0). Métrica de cobertura. */
export const trackSearchResultsReturned = (data: {
  resultCount: number;
  tiempoMs: number;
  pagina: number;
  filtros: Record<string, unknown>;
}) => {
  emit('search_results_returned', {
    result_count: data.resultCount,
    tiempo_de_respuesta_ms: data.tiempoMs,
    pagina: data.pagina,
    filtros: data.filtros,
  });
};

/** #11 — Click en una card de resultado. */
export const trackResultCardClicked = (data: {
  posicion: number;
  prestadorId: string;
  tipoPrestador: string;
  pagina: number;
}) => {
  emit('result_card_clicked', {
    posicion_en_lista: data.posicion,
    prestador_id: data.prestadorId,
    tipo_prestador: data.tipoPrestador,
    pagina: data.pagina,
  });
};

/** #12 — Click en un pin del mapa (clusterizado por ubicación). */
export const trackMapPinClicked = (data: {
  prestadorId: string;
  zoomLevel?: number;
  cantidadEnPin?: number;
}) => {
  emit('map_pin_clicked', {
    prestador_id: data.prestadorId,
    zoom_level: data.zoomLevel,
    cantidad_en_pin: data.cantidadEnPin,
  });
};

/** #13 — Descarga/apertura del PDF de la cartilla. */
export const trackPdfDownloaded = (data: { resultCount: number }) => {
  emit('pdf_downloaded', {
    filtros: currentSearchFilters,
    result_count: data.resultCount,
  });
};

/** #14 — Click en paginación (página 2, 3, ...). */
export const trackPaginationClicked = (data: { paginaDestino: number }) => {
  emit('pagination_clicked', {
    pagina_destino: data.paginaDestino,
    filtros: currentSearchFilters,
  });
};

// =========================================================================
// EVENTOS — Tracking plan Pantalla 4 (Detalle del prestador / contacto)
// =========================================================================

/** #16 — Se abre el detalle/card de un prestador (cierra el funnel de descubrimiento). */
export const trackProviderDetailViewed = (data: {
  prestadorId: string;
  especialidad: string;
  tipoPrestador: string;
  tieneObservaciones: boolean;
  posicionOriginal: number;
}) => {
  emit('provider_detail_viewed', {
    prestador_id: data.prestadorId,
    especialidad: data.especialidad,
    tipo_prestador: data.tipoPrestador,
    tiene_observaciones: data.tieneObservaciones,
    posicion_original: data.posicionOriginal,
  });
};

/** #17 — Click en el teléfono / botón Llamar. */
export const trackNumberClicked = (data: { prestadorId: string; numero: string }) => {
  emit('number_clicked', {
    prestador_id: data.prestadorId,
    numero: data.numero,
    device: getDeviceType(),
  });
};

/** #18 — Click en "Abrir en Maps" / dirección (intención de ir físicamente). */
export const trackMapsClicked = (data: { prestadorId: string }) => {
  emit('maps_clicked', {
    prestador_id: data.prestadorId,
    device: getDeviceType(),
  });
};

// =========================================================================
// EVENTOS — Tracking plan Pantalla 5 (Estado vacío / cero resultados)
// =========================================================================

/** #21 — La búsqueda devolvió 0 resultados (la métrica más importante). */
export const trackZeroResults = (filtros: {
  plan: string;
  provincia: string;
  localidad: string;
  especialidad: string;
  search_type: string;
}) => {
  emit('zero_results', {
    plan: filtros.plan,
    provincia: filtros.provincia,
    localidad: filtros.localidad,
    especialidad: filtros.especialidad,
    search_type: filtros.search_type,
  });
};

/** #22 — Tras un cero, el usuario modifica la búsqueda y reintenta. */
export const trackSearchRefinedFromZero = (data: {
  filtroQueCambio: string[];
  encontro: boolean;
}) => {
  emit('search_refined_from_zero', {
    filtro_que_cambio: data.filtroQueCambio,
    encontro_en_el_reintento: data.encontro,
  });
};

export { mixpanel };
